/**
 * Invoice detail sticky rail — `prototype/sales/invoice-detail.html` ▸ `.rail`.
 *
 * Four cards: next-action (sky-tinted), money, customer, files. Markup
 * follows the prototype verbatim — `.pri-line` / `.actions` / `.btn` /
 * `.btn.primary` / `.btn.secondary` inside `.rail-next`; `.row.grand` /
 * `.row.out` / `.row.paid` with `.v` values inside `.rail-money`;
 * `.who` / `.ava` / `.copy` / `.field` / `.k` / `.v` inside
 * `.rail-party`.
 *
 * Money is computed from a **single source** (the totals shim from the
 * doc) so the rail and the doc-totals can't disagree.
 */
import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Copy,
  Download,
  ExternalLink,
  FileText,
  HandCoins,
  MessageCircle,
  Printer,
  Receipt as ReceiptIcon,
} from "lucide-react";
import { initials } from "../../../lib/initials.ts";
import {
  formatDate,
  formatPhoneIN,
  paiseToInrExact,
  paiseToInrShort,
} from "../../../lib/format.ts";
import type { ShimInvoiceTotals } from "../lib/gst-calc-shim.ts";
import type { InvoiceStatus } from "./invoice-detail-header.tsx";
import { AttachmentsCard } from "../../attachments/attachments-card.tsx";

/** Inputs and callbacks for the invoice sticky right rail. */
export interface InvoiceRailProps {
  invoice: {
    id: string;
    status: InvoiceStatus;
    docNumber: string | null;
    dueOn: string;
  };
  customer: {
    id: string;
    name: string;
    gstin: string | null;
    phone: string | null;
    state: string | null;
    addressLine1: string | null;
    addressLine2: string | null;
    city: string | null;
    country: string | null;
  };
  /** Sole source of truth for the rail's money breakdown. */
  totals: ShimInvoiceTotals;
  /**
   * Receipts allocated against this invoice. The rail surfaces one card per
   * receipt with the receipt id (truncated), date, mode, and applied amount;
   * each row links to `/sales/receipts/$receiptId`. Empty until the first
   * `Record receipt` lands.
   */
  receipts: Array<{
    id: string;
    appliedPaise: string;
    receivedOn?: string;
    mode?: string;
  }>;
  /** Sum of credit-note totals raised against this invoice. */
  creditedPaise: bigint;
  /** True when total credited has reached/exceeded the invoice total. */
  isFullyCredited: boolean;
  onEdit: () => void;
  onIssue: () => void;
  onRecordReceipt: () => void;
  onSendReminder: () => void;
  onCreateCreditNote: () => void;
  onPrint: () => void;
  onClone: () => void;
  onExportTally: () => void;
  /** ADR-0030 permission gates — server enforces; rail hides unavailable actions. */
  canIssue?: boolean;
  canRecordReceipt?: boolean;
  canSend?: boolean;
  canCreateCreditNote?: boolean;
  canExportTally?: boolean;
}

/** Render next-action, money, customer, and files cards for an invoice. */
export function InvoiceRail({
  invoice,
  customer,
  totals,
  receipts,
  creditedPaise,
  isFullyCredited,
  onEdit,
  onIssue,
  onRecordReceipt,
  onSendReminder,
  onCreateCreditNote,
  onPrint,
  onClone,
  onExportTally,
  canIssue = true,
  canRecordReceipt = true,
  canSend = true,
  canCreateCreditNote = true,
  canExportTally = true,
}: InvoiceRailProps) {
  const received = receipts.reduce((sum, receipt) => sum + BigInt(receipt.appliedPaise), 0n);
  const tax = totals.cgstPaise + totals.sgstPaise + totals.igstPaise;
  const total = totals.grandTotalPaise;
  // Outstanding nets receipts AND credit notes — credit notes reduce
  // what the customer still owes per Section 34 of the CGST Act.
  const outstanding = total - received - creditedPaise;
  const isVoided = invoice.status === "void";
  const isPaid = invoice.status === "paid";
  const isReversed = invoice.status === "reversed";

  return (
    <aside className="rail" aria-label="Quick actions and summary">
      {/* Next action */}
      <section className="rail-card rail-next">
        <div className="head">
          <h4>Next action</h4>
          <span className="num" aria-hidden style={{ color: "var(--sky-700)", fontSize: 12 }}>
            {daysUntilLabel(invoice.dueOn, invoice.status)}
          </span>
        </div>
        <div className="body">
          <p className="pri-line">{nextActionCopy(invoice.status, invoice.dueOn, outstanding)}</p>
          <div className="actions">
            {invoice.status === "draft" ? (
              <>
                {canIssue ? (
                  <button type="button" className="btn primary" onClick={onIssue}>
                    Issue and send
                  </button>
                ) : null}
                <button type="button" className="btn secondary" onClick={onEdit}>
                  Edit draft
                </button>
              </>
            ) : isPaid ? (
              <>
                <button type="button" className="btn primary" onClick={onPrint}>
                  <Printer size={12} /> Print
                </button>
                <button type="button" className="btn secondary" onClick={onClone}>
                  <Copy size={12} /> Clone
                </button>
              </>
            ) : isVoided ? null : isReversed ? (
              // Reversed → no receipt to record (CN settled it), no
              // reminder to send. Print for the records + clone if you
              // want to start a fresh corrected invoice.
              <>
                <button type="button" className="btn primary" onClick={onPrint}>
                  <Printer size={12} /> Print
                </button>
                <button type="button" className="btn secondary" onClick={onClone}>
                  <Copy size={12} /> Clone
                </button>
              </>
            ) : (
              <>
                {canRecordReceipt ? (
                  <button type="button" className="btn primary" onClick={onRecordReceipt}>
                    <HandCoins size={12} /> Record receipt
                  </button>
                ) : null}
                {canSend ? (
                  <button type="button" className="btn secondary" onClick={onSendReminder}>
                    <MessageCircle size={12} /> Send reminder
                  </button>
                ) : null}
                {canCreateCreditNote && !isFullyCredited ? (
                  <button type="button" className="btn secondary" onClick={onCreateCreditNote}>
                    <FileText size={12} /> Create credit note
                  </button>
                ) : null}
              </>
            )}
            {/* Tally export + e-Way bill link-out — issued (non-draft, non-void) invoices. */}
            {invoice.status !== "draft" && !isVoided ? (
              <>
                {canExportTally ? (
                  <button type="button" className="btn secondary" onClick={onExportTally}>
                    <Download size={12} /> Export to Tally
                  </button>
                ) : null}
                {/* e-Way bill is generated on the GSTN portal (no URL pre-fill); link out.
                    @see packages/domain/src/compliance/e-way-bill */}
                <a
                  className="btn secondary"
                  href="https://ewaybillgst.gov.in/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink size={12} /> e-Way bill
                </a>
              </>
            ) : null}
          </div>
        </div>
      </section>

      {/* Money */}
      <section className="rail-card rail-money">
        <div className="head">
          <h4>Money</h4>
        </div>
        <div className="body">
          <div className="row">
            <span>Subtotal</span>
            <span className="v">{paiseToInrExact(totals.taxableValuePaise)}</span>
          </div>
          <div className="row">
            <span>{totals.taxMode === "intra" ? "CGST + SGST" : "IGST"}</span>
            <span className="v">{paiseToInrExact(tax)}</span>
          </div>
          <div className="row grand">
            <span>Total invoiced</span>
            <span className="v">{paiseToInrExact(total)}</span>
          </div>
          <div className="row paid">
            <span>Receipts allocated</span>
            <span className="v">{paiseToInrExact(received)}</span>
          </div>
          {creditedPaise > 0n ? (
            <div className="row credited">
              <span>Less: credit notes</span>
              <span className="v">−{paiseToInrExact(creditedPaise)}</span>
            </div>
          ) : null}
          <div className="row out">
            <span>Outstanding</span>
            <span className="v">{paiseToInrExact(outstanding)}</span>
          </div>
        </div>
      </section>

      {/* Receipts — visible once at least one is recorded. Lists each allocation
       *  with id, date, mode, and applied amount. Each row links to the
       *  receipt detail page. */}
      {receipts.length > 0 ? (
        <section className="rail-card rail-receipts">
          <div className="head">
            <h4>
              Receipts <span className="rail-receipts-count">{receipts.length}</span>
            </h4>
            <Link to="/sales/receipts" className="open-link" aria-label="Open receipts list">
              View all <ArrowRight size={11} />
            </Link>
          </div>
          <div className="body">
            <ul className="rail-receipts-list">
              {receipts.map((receipt) => (
                <li key={receipt.id}>
                  <Link
                    to="/sales/receipts/$receiptId"
                    params={{ receiptId: receipt.id }}
                    className="rail-receipt-row"
                  >
                    <span className="rail-receipt-icon" aria-hidden>
                      <ReceiptIcon size={14} strokeWidth={2} />
                    </span>
                    <span className="rail-receipt-main">
                      <span className="rail-receipt-id">RCT-{receipt.id.slice(0, 8)}</span>
                      <span className="rail-receipt-meta">
                        {receipt.receivedOn ? formatDate(receipt.receivedOn) : "—"}
                        {receipt.mode ? ` · ${formatMode(receipt.mode)}` : ""}
                      </span>
                    </span>
                    <span className="rail-receipt-amount">
                      {paiseToInrExact(receipt.appliedPaise)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
            {receipts.length > 1 ? (
              <div className="rail-receipts-total">
                <span>Total received</span>
                <span className="v">{paiseToInrExact(received)}</span>
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      {/* Customer */}
      <section className="rail-card rail-party">
        <div className="head">
          <h4>Customer</h4>
          <Link
            to="/sales/customers/$customerId"
            params={{ customerId: customer.id }}
            className="open-link"
            aria-label="Open customer detail"
          >
            Open <ArrowRight size={11} />
          </Link>
        </div>
        <div className="body">
          <div className="who">
            <div className="ava" aria-hidden>
              {initials(customer.name)}
            </div>
            <div className="copy">
              <Link
                to="/sales/customers/$customerId"
                params={{ customerId: customer.id }}
                className="name"
              >
                {customer.name}
              </Link>
              <div className="meta">{customer.gstin ?? "Unregistered"}</div>
            </div>
          </div>
          <div className="field">
            <span className="k">Phone</span>
            <span className="v">
              {customer.phone ? (
                <a href={`tel:+91${customer.phone}`} className="num">
                  {formatPhoneIN(customer.phone)}
                </a>
              ) : (
                "Not on file"
              )}
            </span>
          </div>
          <div className="field">
            <span className="k">Address</span>
            <span className="v">
              {/* Full address incl. region — country for foreign buyers, state
                  for domestic. Replaces the old state-only row that read
                  "Not on file" for export customers. */}
              {[
                customer.addressLine1,
                customer.addressLine2,
                customer.city,
                customer.country ?? customer.state,
              ]
                .filter(Boolean)
                .join(", ") || "Not on file"}
            </span>
          </div>
        </div>
      </section>

      {/* Files — shared attachment service (owner = this invoice). */}
      <section className="rail-card rail-files">
        <div className="head">
          <h4>Files</h4>
        </div>
        <div className="body">
          <AttachmentsCard ownerType="invoice" ownerId={invoice.id} />
        </div>
      </section>
    </aside>
  );
}

function nextActionCopy(status: InvoiceStatus, dueOn: string, outstanding: bigint): string {
  if (status === "draft") return "Finish the draft and issue it when the tax checks pass.";
  if (status === "issued")
    return "Awaiting payment. A reminder around day 5 helps if the customer is slow.";
  if (status === "partially_paid")
    return `Partially paid. ${paiseToInrShort(outstanding.toString())} outstanding.`;
  if (status === "overdue")
    return `Overdue by ${daysPast(dueOn)}d. Customer typically pays +10d on average.`;
  if (status === "paid") return "Fully paid. Print or clone when needed.";
  return "Voided invoice. Historical record only.";
}

function daysPast(iso: string): number {
  const ms = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.floor(ms / 86_400_000));
}

function daysUntilLabel(iso: string, status: InvoiceStatus): string {
  if (status === "paid" || status === "void") return "";
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
  if (days < 0) return `${Math.abs(days)}d late`;
  return `${days}d`;
}

/** Receipt mode codes → display labels. */
function formatMode(mode: string): string {
  switch (mode) {
    case "upi":
      return "UPI";
    case "neft":
      return "NEFT";
    case "rtgs":
      return "RTGS";
    case "imps":
      return "IMPS";
    case "cheque":
      return "Cheque";
    case "cash":
      return "Cash";
    case "wire":
      return "Wire";
    case "other":
      return "Other";
    default:
      return mode;
  }
}
